import React, {
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	Input,
	message,
	Pagination,
	Table,
} from 'antd';

import {
	EyeInvisibleOutlined,
	EyeOutlined,
} from '@ant-design/icons';

import { debounce } from 'lodash';
import moment from 'moment';

import { IoSearchOutline } from 'react-icons/io5';

import { api } from '../../../api/api';

import SuccessIcon from '../../../assets/successicon.svg';

import styles from './completedModifyNric.module.scss';

const CompletedModifyNric = () => {
	const userDetails = JSON.parse(
		localStorage.getItem('user-onboard-data')
	);

	const isAdmin =
		userDetails?.isAdmin === 'Y';

	const [data, setData] =
		useState([]);

	const [loading, setLoading] =
		useState(false);

	const [searchText, setSearchText] =
		useState('');

	const [visibleNric, setVisibleNric] =
		useState({});

	const [tableParams, setTableParams] =
		useState({
			pagination: {
				current: 1,
				pageSize: 10,
				total: 0,
			},
		});

	/*
	 * =========================================
	 * NRIC VISIBILITY
	 * =========================================
	 */
	const toggleNric = (key) => {
		setVisibleNric((prev) => {
			const isNowVisible =
				!prev[key];

			if (isNowVisible) {
				setTimeout(() => {
					setVisibleNric(
						(current) => ({
							...current,
							[key]: false,
						})
					);
				}, 10000);
			}

			return {
				...prev,
				[key]: isNowVisible,
			};
		});
	};

	/*
	 * =========================================
	 * FETCH COMPLETED LIST
	 * =========================================
	 *
	 * Admin:
	 * getAdminAllModifyNricCompletedList
	 *
	 * Normal User:
	 * getUserAllModifyNricCompletedList
	 */
	const fetchCompletedList = async () => {
	setLoading(true);

	try {
		const {
			current,
			pageSize,
		} = tableParams.pagination;

		const response = await api.get({
			path:
				userDetails?.isAdmin === 'Y'
					? '/getAdminAllModifyNricCompletedList'
					: `/getUserAllModifyNricCompletedList?nhName=${userDetails?.nhName}`,

			params: {
				page: current,
				size: pageSize,
				searchText,
			},

			service: 'useronboarding',
		});

		if (response.status === 200) {
			const completedList =
				response.data?.data || [];

			const totalData =
				response.data?.pagination?.totalData;

			setData(completedList);

			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					total:
						totalData ||
						response.data?.data?.length,
				},
			}));
		}

	} catch (error) {
		console.error(
			'Completed NRIC List Error:',
			error
		);

		message.error(
			'Unable to retrieve completed NRIC modification requests.'
		);

	} finally {
		setLoading(false);
	}
};

	useEffect(() => {
		fetchCompletedList();

		// eslint-disable-next-line
	}, [
		tableParams.pagination.current,
		tableParams.pagination.pageSize,
		searchText,
	]);

	/*
	 * =========================================
	 * PAGINATION
	 * =========================================
	 */
	const handleTableChange = (
		pagination
	) => {
		setTableParams(
			(prev) => ({
				...prev,

				pagination: {
					...prev.pagination,

					...pagination,
				},
			})
		);
	};

	/*
	 * =========================================
	 * SEARCH
	 * =========================================
	 */
	const handleSearch = (e) => {
		const value =
			e.target.value || '';

		const trimmedValue =
			value.trim();

		if (
			trimmedValue.length >= 3 ||
			trimmedValue.length === 0
		) {
			setSearchText(
				trimmedValue
			);

			setTableParams(
				(prev) => ({
					...prev,

					pagination: {
						...prev.pagination,

						current: 1,
					},
				})
			);
		}
	};

	const debouncedSearch =
		useRef(
			debounce(
				handleSearch,
				300
			)
		).current;

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	/*
	 * =========================================
	 * STATUS
	 * =========================================
	 */
	const getStatusTag = (
		status
	) => {
		switch (
			String(
				status || ''
			).toUpperCase()
		) {
			case 'C':
				return (
					<span
						className={
							styles.badgeCompleted
						}
					>
						Completed
					</span>
				);

			case 'F':
				return (
					<span
						className={
							styles.badgeFailed
						}
					>
						Failed
					</span>
				);

			case 'R':
				return (
					<span
						className={
							styles.badgeRejected
						}
					>
						Rejected
					</span>
				);

			default:
				return (
					<span
						className={
							styles.badgeUnknown
						}
					>
						Unknown
					</span>
				);
		}
	};

	/*
	 * =========================================
	 * NRIC DISPLAY
	 * =========================================
	 */
	const renderNric = (
		value,
		record,
		type
	) => {
		if (!value) {
			return '-';
		}

		const key =
			`${record.id}-${type}`;

		const isVisible =
			visibleNric[key];

		const masked =
			value.replace(
				/.(?=.{4})/g,
				'*'
			);

		return (
			<div
				style={{
					display:
						'flex',

					alignItems:
						'center',

					gap:
						8,
				}}
			>
				<span>
					{
						isVisible
							? value
							: masked
					}
				</span>

				{
					isVisible
						? (
							<EyeInvisibleOutlined
								className={
									styles.nricEyeIcon
								}

								onClick={() =>
									toggleNric(
										key
									)
								}
							/>
						)
						: (
							<EyeOutlined
								className={
									styles.nricEyeIcon
								}

								onClick={() =>
									toggleNric(
										key
									)
								}
							/>
						)
				}
			</div>
		);
	};

	/*
	 * =========================================
	 * TABLE COLUMNS
	 * =========================================
	 */
	const columns = [
		{
			title:
				'Status',

			dataIndex:
				'status',

			key:
				'status',

			width:
				120,

			render:
				getStatusTag,
		},

		{
			title:
				'Nursing Home',

			dataIndex:
				'nhName',

			key:
				'nhName',

			minWidth:
				200,
		},

		{
			title:
				'Username',

			dataIndex:
				'userName',

			key:
				'userName',

			minWidth:
				180,
		},
        {
            title: 'New Username',
            dataIndex: 'newUserName',
            key: 'newUserName',
            minWidth: 180,
            render: (value) => value || '-',
        },
		{
			title:
				'Full Name',

			dataIndex:
				'fullName',

			key:
				'fullName',

			minWidth:
				250,
		},

		{
			title:
				'Old NRIC / FIN',

			dataIndex:
				'oldNric',

			key:
				'oldNric',

			minWidth:
				190,

			render: (
				value,
				record
			) =>
				renderNric(
					value,
					record,
					'old'
				),
		},

		{
			title:
				'New NRIC / FIN',

			dataIndex:
				'newNric',

			key:
				'newNric',

			minWidth:
				190,

			render: (
				value,
				record
			) =>
				renderNric(
					value,
					record,
					'new'
				),
		},
        {
            title: 'Completion Email List',
            dataIndex: 'completionEmailList',
            key: 'completionEmailList',
            minWidth: 280,
            render: (value) => {
                if (!value) {
                    return '-';
                }

                return String(value)
                    .split(',')
                    .map((email) => (
                        <div key={email.trim()}>
                            {email.trim()}
                        </div>
                    ));
            },
        },
		{
			title:
				'Submitted By',

			dataIndex:
				'createdBy',

			key:
				'createdBy',

			minWidth:
				180,
		},

		{
			title:
				'Submitted Date',

			dataIndex:
				'createdDate',

			key:
				'createdDate',

			minWidth:
				180,

			render:
				(value) =>
					value
						? moment(
								value
							).format(
								'DD-MMM-YYYY HH:mm'
							)
						: '-',
		},

		{
			title:
				isAdmin
					? 'Processed By'
					: 'Processed By',

			dataIndex:
				'modifiedBy',

			key:
				'modifiedBy',

			minWidth:
				180,

			render:
				(value) =>
					value || '-',
		},

		{
			title:
				'Processed Date',

			dataIndex:
				'modifiedDate',

			key:
				'modifiedDate',

			minWidth:
				180,

			render:
				(value) =>
					value
						? moment(
								value
							).format(
								'DD-MMM-YYYY HH:mm'
							)
						: '-',
		},

		{
			title:
				'Remarks',

			dataIndex:
				'remarks',

			key:
				'remarks',

			minWidth:
				350,

			render:
				(value) =>
					value || '-',
		},
	];

	return (
		<div
			className={
				styles['wrapper']
			}
		>
			{/* HEADER */}
			<div
				className={
					styles[
						'top-section'
					]
				}
			>
				<div
					className={
						styles[
							'top-section-left'
						]
					}
				>
					<div
						className={
							styles['left']
						}
					>
						<p
							className={
								styles['title']
							}
						>
							Completed NRIC Modification
						</p>

						<div
							className={
								styles['totals']
							}
						>
							<img
								className={
									styles[
										'success-icons'
									]
								}

								src={
									SuccessIcon
								}

								alt="Success Icon"
							/>

							<p
								className={
									styles[
										'totals'
									]
								}
							>
								Total Requests :{' '}
								{
									tableParams
										.pagination
										.total
								}
							</p>
						</div>
					</div>

					<div
						className={
							styles['right']
						}
					>
						<Input
							prefix={
								<IoSearchOutline />
							}

							className={
								styles[
									'search-input'
								]
							}

							placeholder="Search by full name, username or new username"

							onPressEnter={
								handleSearch
							}

							onChange={
								debouncedSearch
							}

							allowClear
						/>
					</div>
				</div>
			</div>

			{/* TABLE */}
			<div
				className={
					styles.horizontalFixed
				}
			>
				<div
					className={
						styles.verticalScroll
					}
				>
					<Table
						className={
							styles.table
						}

						rowKey={
							(record) =>
								record.id
						}

						dataSource={
							data
						}

						columns={
							columns
						}

						loading={
							loading
						}

						pagination={
							false
						}

						tableLayout="auto"

						locale={{
							emptyText: (
								<div
									style={{
										padding:
											'40px 0',

										textAlign:
											'center',
									}}
								>
									No completed NRIC modification requests found
								</div>
							),
						}}

						rowClassName={(
							_,
							index
						) =>
							index % 2 === 0
								? styles.evenRow
								: styles.oddRow
						}

						scroll={{
							x:
								'max-content',

							y:
								450,
						}}
					/>
				</div>
			</div>

			{/* PAGINATION */}
			<div
				className={
					styles.paginationWrapper
				}
			>
				<Pagination
					current={
						tableParams
							.pagination
							.current
					}

					pageSize={
						tableParams
							.pagination
							.pageSize
					}

					total={
						tableParams
							.pagination
							.total
					}

					showSizeChanger

					showQuickJumper

					hideOnSinglePage

					onChange={(
						page,
						pageSize
					) =>
						handleTableChange({
							current:
								page,

							pageSize,
						})
					}
				/>
			</div>
		</div>
	);
};

export default CompletedModifyNric;